<script>
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import NotePercent from './NotePercent.svelte';
  import IgnorantBar from './IgnorantBar.svelte';
  import { scaleSqrt } from 'd3-scale';
  import {overviewURL, debounce, reduceData, getStickerSize} from './settings';
  let loading = true;
  let error = false;
  let totalCount = null;
  let displayTotalCount = null;
  let meloniChartData = [];
  let schleinChartData = [];
  let content = {};
  let bars = [];
  let labels = []

  const draw = () => {
    const size = getStickerSize();
    totalCount = content.data.count;
    displayTotalCount = new Intl.NumberFormat('it-IT', (content.data.count < 10000 ? {} : { notation: 'compact' })).format(totalCount);
    const radius = scaleSqrt()
      .domain([0, 1])
      .range(size);
    
    meloniChartData = reduceData('meloni', radius, content, totalCount);
    schleinChartData = reduceData('schlein', radius, content, totalCount);
    const allMeloni = meloniChartData.reduce((a, d) => a += d.v, 0) / totalCount * 100;
    const allSchlein = schleinChartData.reduce((a, d) => a += d.v, 0) / totalCount * 100;
    bars = [
      {  
        w:  allMeloni ,
        c: 'meloni'
      },
      {
        w: allSchlein,
        c: 'schlein'
      }
    ];
    labels = [
      {
        c: 'meloni',
        l: `Meloni: ${new Intl.NumberFormat('it-IT', { style: 'percent' }).format(allMeloni / 100)}`
      },
      {
        c: 'overlap',
        l: `${new Intl.NumberFormat('it-IT', { style: 'percent' }).format(((allMeloni + allSchlein) - 100) / 100)}`
      },
      {
        c: 'schlein',
        l: `Schlein: ${new Intl.NumberFormat('it-IT', { style: 'percent' }).format(allSchlein / 100)}`
      }
    ];
    loading = false;
  };

  const debouncedSetWindowWidth = debounce(draw, 300);

  onMount(async () => {
    try {
      const response = await fetch(overviewURL);
      content = await response.json();
      if (content.error) {
        console.warn(content.error);
        loading = false;
        error = true;
      } else {
        draw();       
      }
    } catch(e) {
      console.warn(e);
      loading = false;
      error = true;
    }
    window.addEventListener('resize', debouncedSetWindowWidth);		
		return () => {
			window.removeEventListener('resize', debouncedSetWindowWidth);
		}
  });
</script>

<div id="overview" class="{loading ? 'loading' : ''}">
  {#if error}
    <p class="error">C'è stato un problema nel caricamento dei dati, per favore riprova più tardi.</p>
  {:else}
    <div class="overview--content">
      <div class="overview--chart">
        <div class="overview--bar">
          <IgnorantBar bars={bars} labels={labels} />
        </div>
      </div>
      <div class="overview--chart">
        <div class="overview--meloni">        
          {#each meloniChartData as m, index}
            <div
              class="overview--icon icon-{index} {m.v === 0 ? 'zero' : ''}"
              style="width: {m.w}px; height: {m.w}px">
              <Icon sentiment={m.sentiment} src="meloni" />
              <div class="value {m.sentiment}">{m.val}</div>
              {#if index === 0}<h3>Giorgia Meloni</h3>{/if}
            </div>
          {/each}
        </div>
        <div class="overview--schlein">
          {#each schleinChartData as s, index}
            <div
              class="overview--icon icon-{index} {s.v === 0 ? 'zero' : ''}"
              style="width: {s.w}px; height: {s.w}px">
              <Icon sentiment={s.sentiment} src="schlein" />
              <div class="value {s.sentiment}">{s.val}</div>
              {#if index === 0}<h3>Elly Schlein</h3>{/if}
            </div>
          {/each}
        </div>
      </div>
      <NotePercent />
    </div>
  {/if}
</div>

<style>
  #overview {
    align-items: center;
    display: flex;
    height: auto;
    justify-content: center;
    margin: 0 20px;
    opacity: 1;
    padding: 5vh 0 10vh 0;
    transition: opacity .5s ease-in-out;
    width: auto;
  }

  #overview.loading {
    opacity: 0;
  }

  .overview--content {
    width: 100%;
  }

  .overview--chart {
    display: flex;
    flex-direction: column;
    height: auto;
    justify-content: space-between;
    width: 100%;
  }

  .overview--meloni,
  .overview--schlein {
    flex: 0 0 40vh;
    height: 40vh;
    position: relative;
  }

  .overview--icon {
    display: block;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
  }

  .overview--icon.icon-0 {
    z-index: 3;
  }

  .overview--icon.icon-1 {
    left: 20%;
    z-index: 2;
  }

  .overview--icon.icon-2 {
    left: 80%;
    z-index: 1;
  }

  .overview--icon .value {
    color: var(--neutral);
    font-family: 'Fira Mono', monospace;
    font-weight: 500;
    left: 50%;
    position: absolute;
    text-align: center;
    top: 100%;
    transform: translate3d(-50%, 0, 0);
  }

  .overview--icon h3 {
    bottom: 100%;
    left: 50%;
    margin: 0;
    position: absolute;
    transform: translate3d(-50%, -10px, 0);
  }

  .overview--icon .value.positive {
    color: var(--positive);
  }

  .overview--icon .value.negative {
    color: var(--negative);
  }

  @media screen and (min-width: 768px) {
    #overview {
      max-width: 960px;
    }
  }

  .overview--bar {
    display: block;
    flex: 0 0 20vh;
    width: 100%;
  }

  @media screen and (min-width: 1024px) {
    #overview {
      margin: 0 auto;
      width: 100%;
    }

    .overview--chart {
      flex-direction: row;
      flex-wrap: wrap;
      height: auto;
    }

    .overview--bar {
      flex: 0 0 100%;
    }

    .overview--meloni,
    .overview--schlein {
      flex: 0 0 45%;
      height: 380px;
      padding-top: 100px;
    }
  }
</style>